import {
  OsLotReviewLink,
  OsLotReviewListItem,
  OsLotReviewQueueStatus,
} from '../../lib/types/os-lot-reviews';
import {
  areaDelta,
  formatAreaHa,
  formatNumberFr,
  formatSignedDelta,
  formatTonnage,
  formatYield,
  getAreaDeltaValue,
  getTonnageDeltaValue,
  tonnageDelta,
} from '../lots-validation-table/lots-formatters';

/** Keep in sync with `back-end/apps/accounts/os_lot_review.py`. */
export const YIELD_OK_RANGES_BY_SPECIES: Record<
  string,
  readonly [number, number]
> = {
  BTH: [4.0, 11.0],
  MIS: [5.0, 13.0],
};
export const YIELD_OK_DEFAULT: readonly [number, number] = [1.0, 6.0];
export const TONNAGE_DELTA_ABS_MAX = 5.0;
export const AREA_DELTA_MIN_OK = -10.0;

export type ReviewMetricKind = 'yield' | 'tonnage' | 'area';

export type ReviewMetric = {
  kind: ReviewMetricKind;
  fail: boolean;
  missing: boolean;
  value: string;
  delta: string | null;
  reference: string | null;
  rangeMin: string;
  rangeMax: string;
  tonnageThreshold: string;
  areaThreshold: string;
};

export type LotReviewMetrics = {
  yield: ReviewMetric;
  tonnage: ReviewMetric;
  area: ReviewMetric;
};

export function canAttestOsLotReview(lot: {
  all_ok: boolean;
  links_count: number;
  links_pending_count: number;
}) {
  return lot.all_ok && lot.links_count > 0 && lot.links_pending_count > 0;
}

export function reviewStatusColor(
  status: OsLotReviewQueueStatus,
): 'pf-green' | 'orange' | 'gray' {
  if (status === 'attested') {
    return 'pf-green';
  }
  if (status === 'correction_proposed') {
    return 'orange';
  }
  return 'gray';
}

export function getYieldOkRange(
  speciesCode: string | null | undefined,
): readonly [number, number] {
  if (speciesCode && YIELD_OK_RANGES_BY_SPECIES[speciesCode]) {
    return YIELD_OK_RANGES_BY_SPECIES[speciesCode];
  }
  return YIELD_OK_DEFAULT;
}

function isPresent(value: number | null | undefined): value is number {
  return value != null && !Number.isNaN(value);
}

function yieldOutOfRange(
  value: number | null | undefined,
  range: readonly [number, number],
): boolean {
  if (!isPresent(value)) {
    return true;
  }
  return value < range[0] || value > range[1];
}

function metricShell(
  kind: ReviewMetricKind,
  range: readonly [number, number],
): Pick<
  ReviewMetric,
  'kind' | 'rangeMin' | 'rangeMax' | 'tonnageThreshold' | 'areaThreshold'
> {
  return {
    kind,
    rangeMin: formatNumberFr(range[0]),
    rangeMax: formatNumberFr(range[1]),
    tonnageThreshold: formatNumberFr(TONNAGE_DELTA_ABS_MAX),
    areaThreshold: formatNumberFr(AREA_DELTA_MIN_OK),
  };
}

export function getLotReviewMetrics(
  lot: Pick<
    OsLotReviewListItem,
    | 'species'
    | 'real_yield'
    | 'yield_ok'
    | 'real_tonnage'
    | 'oleoze_tonnage'
    | 'tonnage_ok'
    | 'tonnage_delta'
    | 'calculated_area'
    | 'theoretical_area'
    | 'area_ok'
    | 'area_delta'
  >,
): LotReviewMetrics {
  const range = getYieldOkRange(lot.species?.code);
  const tonnageDeltaValue = getTonnageDeltaValue(lot);
  const areaDeltaValue = getAreaDeltaValue(lot);

  return {
    yield: {
      ...metricShell('yield', range),
      fail: !lot.yield_ok,
      missing: !isPresent(lot.real_yield),
      value: formatYield(lot.real_yield),
      delta: null,
      reference: `${formatNumberFr(range[0])}–${formatNumberFr(range[1])} t/ha`,
    },
    tonnage: {
      ...metricShell('tonnage', range),
      fail: !lot.tonnage_ok,
      missing: !isPresent(tonnageDeltaValue),
      value: formatTonnage(lot.real_tonnage),
      delta: formatSignedDelta(tonnageDeltaValue, 't'),
      reference: formatTonnage(lot.oleoze_tonnage),
    },
    area: {
      ...metricShell('area', range),
      fail: !lot.area_ok,
      missing: !isPresent(areaDeltaValue),
      value: formatAreaHa(lot.calculated_area),
      delta: formatSignedDelta(areaDeltaValue, 'ha'),
      reference: formatAreaHa(lot.theoretical_area),
    },
  };
}

export function getLinkReviewMetrics(
  link: OsLotReviewLink,
  lot: Pick<
    OsLotReviewListItem,
    'species' | 'theoretical_area' | 'links_count'
  >,
): LotReviewMetrics {
  const range = getYieldOkRange(lot.species?.code);
  const comparableArea = lot.links_count <= 1;
  const volumeDelta = tonnageDelta(link.volume_reel, link.oleoze_volume).absolute;
  const declaredAreaDelta = comparableArea
    ? areaDelta(link.area_reel, lot.theoretical_area)
    : null;
  const yieldMissing = !isPresent(link.rdt_reel);
  const yieldFail = yieldOutOfRange(link.rdt_reel, range);
  const tonnageMissing = !isPresent(volumeDelta);
  const tonnageFail =
    !isPresent(volumeDelta) || Math.abs(volumeDelta) > TONNAGE_DELTA_ABS_MAX;
  const areaMissing = comparableArea && !isPresent(declaredAreaDelta);
  const areaFail =
    comparableArea &&
    (!isPresent(declaredAreaDelta) || declaredAreaDelta < AREA_DELTA_MIN_OK);

  return {
    yield: {
      ...metricShell('yield', range),
      fail: yieldFail,
      missing: yieldMissing,
      value: formatYield(link.rdt_reel),
      delta: null,
      reference: isPresent(link.oleoze_yield)
        ? formatYield(link.oleoze_yield)
        : null,
    },
    tonnage: {
      ...metricShell('tonnage', range),
      fail: tonnageFail,
      missing: tonnageMissing,
      value: formatTonnage(link.volume_reel),
      delta: formatSignedDelta(volumeDelta, 't'),
      reference: isPresent(link.oleoze_volume)
        ? formatTonnage(link.oleoze_volume)
        : null,
    },
    area: {
      ...metricShell('area', range),
      fail: areaFail,
      missing: areaMissing,
      value: formatAreaHa(link.area_reel),
      delta: comparableArea ? formatSignedDelta(declaredAreaDelta, 'ha') : null,
      reference: formatAreaHa(lot.theoretical_area),
    },
  };
}
